import {
  LayoutServicePageState,
  Placeholder,
  Text,
  TextField,
  useSitecoreContext,
  Image,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import Icon from 'components/helpers/Icon/Icon';
import Loader from 'components/helpers/Loader/Loader';

import { dataFetcher } from 'lib/data-fetcher';
import compose from 'lib/enhancers/compose';
import type { ComponentProps } from 'lib/component-props';
import withStyles from 'lib/enhancers/withStyles';

import mockPlanData from './MockData/mock.data.plans.json';
import styles from './PlanOptionsListingGraphQL.module.scss';

import { getQueryStringParameter, saveQueryStrings, getSavedPathWithQueryStrings } from 'lib/utils';
import { buildURL, buildApplyToEnrollURL, checkEnrollEnable } from 'components/PlanData/utilities';
import { useI18n } from 'next-localization';
import { County } from '../Types/Counties.Type';
import { Plan } from '../Types/Plans.Type';
import { PlanYear } from '../Types/PlanYear.Type';

type PlanOptionsListingProps = ComponentProps & {
  fields: {
    counties: County[];
    searchpageurl: string;
    detailpageurl: string;
    comparepageurl: string;
    planYears: PlanYear[];
    planType: Plan;
    title: string;
    description: string;
    maxCompare: number;
    compareError: string;
    compareSelect: string;
    plansOrder: string[];
  };
};

const cx = classNames.bind(styles);

const PlanOptionsListing = (props: PlanOptionsListingProps): JSX.Element => {
  const { className, fields, rendering } = props;
  const { t } = useI18n();

  const { sitecoreContext } = useSitecoreContext();
  const router = useRouter();

  const [countyLookup, setCountyLookup] = useState<County>();
  const [plans, setPlans] = useState<PlanResponseData[]>([]);
  const [enableEnroll, setEnableEnroll] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);

  const planYear = React.useRef('');

  const compareErrorMessage = fields.compareError?.replaceAll('{max}', fields.maxCompare.toString());
  const compareSelectMessage = fields.compareSelect?.replaceAll('{max}', fields.maxCompare.toString());

  useEffect(() => {
    const queryStringKey = 'planoptions-qs';

    const sortPlansData = (plansList: PlanResponseData[]): PlanResponseData[] => {
      const tempList: PlanResponseData[] = [];
      plansList.forEach((plan) => {
        if (tempList.find((find) => find.id == plan.id) == undefined) {
          tempList.push(plan);
        }
      });
      if (!fields.plansOrder?.length) {
        return plansList;
      }

      const sortedPlansList = tempList.slice().sort((a, b) => {
        const indexA = fields.plansOrder?.indexOf(a.id);
        const indexB = fields.plansOrder?.indexOf(b.id);

        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

      return sortedPlansList;
    };

    let planYearId = '';
    let countyId = '';
    let allParamsFound = false;

    if (sitecoreContext.pageState === LayoutServicePageState.Normal) {
      if (router.isReady) {
        if (router.asPath?.indexOf('?') > 0) {
          const countyName = getQueryStringParameter('county', router.query);
          const stateAbbrev = getQueryStringParameter('state', router.query);
          planYear.current = getQueryStringParameter('year', router.query);

          /**
           * Verify we have all 3 parameters required in the query string.
           * If we do, next need to validate we can find corresponding sitecore
           * items.
           */
          if (countyName && stateAbbrev && planYear.current) {
            const matchingYear = fields.planYears?.find((x) => x.year.toString() === planYear.current);

            setEnableEnroll(checkEnrollEnable(fields.planYears, parseInt(planYear.current)));

            if (matchingYear) {
              planYearId = matchingYear.id;
            }

            const matchingCounty = fields.counties.find((x) => {
              return (
                x.state?.toLowerCase() == stateAbbrev.toLowerCase() &&
                x.countyName?.toLowerCase() === countyName.toLowerCase()
              );
            });

            if (matchingCounty) {
              countyId = matchingCounty.id;
              setCountyLookup(matchingCounty);

              // need county and year
              if (matchingYear) {
                allParamsFound = true;
              }
            }
          }
        }

        if (allParamsFound) {
          saveQueryStrings(router, queryStringKey);
        } else {
          const savedUrl = getSavedPathWithQueryStrings(queryStringKey);
          const redirectPath = savedUrl || fields.searchpageurl;
          router.push(redirectPath);
        }
      }
      const baseUrl = process.env['PUBLIC_URL'] || '';

      if (allParamsFound) {
        dataFetcher<PlanResponseData[]>(`${baseUrl}/api/getPlansOptions`, {
          countyId: countyId,
          planTypeId: fields.planType?.id,
          planYearId: planYearId,
          language: sitecoreContext.language,
        }).then((r) => {
          if (r.data && r.data.length > 0) {
            setPlans(sortPlansData(r.data));
          } else {
            router.push(fields.searchpageurl);
          }
        });
      }
    } else {
      setEnableEnroll(true);
      setPlans(mockPlanData as PlanResponseData[]);
    }
  }, [fields, router, setPlans, checked, setChecked, sitecoreContext]);

  const getUrlForPlanId = (planid: string) => {
    const countyName = getQueryStringParameter('county', router.query);
    const zipcode = getQueryStringParameter('zipcode', router.query);
    const languagePrefix = sitecoreContext?.language ? `/${sitecoreContext.language}` : '';
    const url = `${languagePrefix}${props.fields.detailpageurl}?planId=${planid}&county=${countyName}&year=${planYear.current}&zipcode=${zipcode}`;

    return url;
  };

  const handleSubmit = (planid: string) => {
    window.location.href = getUrlForPlanId(planid);
    return false;
  };

  const handleApplyToEnroll = (plan: PlanResponseData) => {
    window.location.href = buildApplyToEnrollURL(
      fields.planType.planName.value,
      getQueryStringParameter('zipcode', router.query),
      getQueryStringParameter('county', router.query),
      getQueryStringParameter('year', router.query),
      plan.planName.value,
      plan.contract.value,
      plan.pbp.value,
      plan.groupId.value,
      plan.cavulusPlanName.value
    );
    return false;
  };

  const handleCompare = () => {
    const planIDs: [string, string][] = Array(checked.length);

    for (let i = 0; i < checked.length; i++) {
      planIDs.push(['plan' + i, checked[i]]);
    }

    const url = buildURL(fields.comparepageurl, router.query, planIDs, sitecoreContext.language);
    window.location.href = url;

    return false;
  };

  const handleCheck = (planid: string) => {
    setChecked((prevState) => {
      // check if it is already added
      if (prevState.includes(planid)) {
        // clone the prevState arr to prevent side effects
        const clone = [...prevState];
        // Remove the existing id
        clone.splice(prevState.indexOf(planid), 1);
        return clone;
      } else {
        return [...prevState, planid];
      }
    });
  };

  const replaceTokens = (text?: string): string => {
    const count = plans.length?.toString();
    let countyName = countyLookup?.countyName ?? '';
    const planType = fields.planType?.planName.value ?? '';

    if (sitecoreContext.pageState !== LayoutServicePageState.Normal) {
      countyName = '[this]';
    }

    return text?.replaceAll('{count}', count).replaceAll('{county}', countyName).replaceAll('{type}', planType) ?? '';
  };

  return (
    <>
      <div role="status">
        {plans?.length > 0 ? <span className={cx('screen-reader-only')}>Plan options loaded</span> : <Loader />}
      </div>
      <section className={cx('plan-options-listing', className)}>
        {plans?.length > 0 && (
          <>
            <div className={cx('plan-options-listing__img-container')}>
              {countyLookup?.image ? <Image alt={countyLookup?.countyName} field={countyLookup?.image} /> : ''}
            </div>
            <header className={cx('plan-options-listing__results')}>
              <Text encode={false} field={{ value: replaceTokens(fields.title) }} tag="h2" />
              <Text encode={false} field={{ value: replaceTokens(fields.description) }} tag="p" />
            </header>
            <div className={cx('plan-options-listing__results-wrapper')}>
              {plans.map((plan, planIndex) => {
                const { featuredPlanEntries, planName, starRating } = plan;
                const planKey = `${planName}-${planIndex}`;

                const stars = [];
                const ratingValue = starRating?.targetItem?.value?.value
                  ? Number(starRating.targetItem.value?.value)
                  : 0;

                for (let num = 0; num < ratingValue; num++) {
                  stars.push(
                    <Icon
                      className={cx({
                        'plan-options-listing__plan-rating-star': true,
                        'plan-options-listing__plan-rating-star--half': ratingValue - num === 0.5,
                      })}
                      key={`${planKey}-star-${num}`}
                      variant="star-filled"
                    />
                  );
                }

                return (
                  <section className={cx('plan-options-listing__plan')} key={planKey}>
                    <header className={cx('plan-options-listing__plan-header')}>
                      <a href={getUrlForPlanId(plan.id)}>
                        <h3 className={cx('plan-options-listing__plan-header-text')}>
                          <span dangerouslySetInnerHTML={{ __html: planName.value ? planName.value.toString() : '' }} />
                          <Icon className={cx('plan-options-listing__plan-header-carat')} variant="carat"></Icon>
                        </h3>
                      </a>
                      {ratingValue ? (
                        <span
                          aria-label={t('Aria-Start-Rating').replace('[rating]', ratingValue.toString())}
                          className={cx('plan-options-listing__plan-rating')}
                        >
                          {stars}
                        </span>
                      ) : (
                        ''
                      )}
                    </header>
                    <article className={cx('plan-options-listing__plan-content')}>
                      <ul className={cx('plan-options-listing__plan-entries')}>
                        {featuredPlanEntries?.targetItems?.map((item, itemIndex) => {
                          const { entryGroup, value } = item;
                          const itemKey = `${planKey}-${entryGroup?.label}-${itemIndex}`;

                          return (
                            <li className={cx('plan-options-listing__plan-entry')} key={itemKey}>
                              {entryGroup?.image?.jsonValue.value.src ? (
                                <img
                                  alt={entryGroup?.image?.jsonValue?.value?.alt}
                                  className={cx('plan-options-listing__plan-entry-icon')}
                                  src={entryGroup?.image?.jsonValue.value.src}
                                />
                              ) : (
                                ''
                              )}

                              <div
                                className={
                                  !entryGroup?.image?.jsonValue.value.src
                                    ? cx('plan-options-listing__plan-entry-label-no-image')
                                    : ''
                                }
                              >
                                <Text
                                  className={cx('font-style-md', 'plan-options-listing__plan-entry-label-heading')}
                                  editable={false}
                                  encode={false}
                                  field={entryGroup?.label}
                                  tag="h4"
                                />
                                <Text editable={false} encode={false} field={value} tag="p" />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </article>
                    <footer className={cx('plan-options-listing__plan-footer')}>
                      <div className={cx('plan-options-listing__footer-buttons')}>
                        <Button
                          className={cx('plan-options-listing__view-button')}
                          onClick={() => {
                            handleSubmit(plan.id);
                          }}
                        >
                          {t('View Plan Details')}
                        </Button>
                        {enableEnroll && (
                          <Button
                            className={cx('plan-options-listing__apply-to-enroll-button')}
                            variant={ButtonVariant.Tertiary}
                            onClick={() => {
                              handleApplyToEnroll(plan);
                            }}
                          >
                            {t('Apply to Enroll')}
                          </Button>
                        )}
                      </div>
                      <section className={cx('plan-options-listing__compare-section')}>
                        <div className={cx('plan-options-listing__compare-checkbox')}>
                          <input
                            type="checkbox"
                            id={`${plan.id}-checkbox`}
                            key={`${plan.id}-checkbox`}
                            checked={checked.indexOf(plan.id) > -1}
                            disabled={checked.length >= fields.maxCompare && checked.indexOf(plan.id) < 0}
                            onChange={() => handleCheck(plan.id)}
                          />
                          <label htmlFor={`${plan.id}-checkbox`}>{t('Compare')}</label>
                        </div>
                        <div role="status" className={cx('plan-options-listing__compare-summary')}>
                          {checked.length >= fields.maxCompare && checked.indexOf(plan.id) < 0 ? (
                            <span>{compareErrorMessage}</span>
                          ) : (
                            ''
                          )}
                          {checked.length === 1 ? <span>{compareSelectMessage}</span> : ''}
                          {checked.length > 1 && checked.indexOf(plan.id) > -1 ? (
                            <Button
                              variant={ButtonVariant.Green}
                              className={cx('plan-options-listing__view-button')}
                              onClick={() => {
                                handleCompare();
                              }}
                            >
                              {t('Compare Plans')}
                            </Button>
                          ) : (
                            ''
                          )}
                        </div>
                      </section>
                    </footer>
                  </section>
                );
              })}
            </div>
          </>
        )}
        <aside className={cx('plan-options-listing__aside')}>
          <Placeholder key={0} name="hp-container-3070-right" rendering={rendering} />
        </aside>
      </section>
    </>
  );
};

type PlanResponseData = {
  id: string;
  planName: TextField;
  planDescription: TextField;
  contract: TextField;
  groupId: TextField;
  pbp: TextField;
  cavulusPlanName: TextField;
  featuredPlanEntries: FeaturedPlanEntries;
  starRating: StarRating;
};

type TargetItem = {
  value: TextField;
  entryGroup: EntryGroup;
};

type FeaturedPlanEntries = {
  targetItems: TargetItem[];
};

type EntryGroup = {
  label: TextField;
  image: Image;
};

type ImageDataWrapper = {
  value: ImageData;
};

type ImageData = {
  src: string;
  alt: string;
  width: string;
  height: string;
};

type Image = {
  jsonValue: ImageDataWrapper;
};

type StarRating = {
  targetItem: StarRatingTargetItem;
};

type StarRatingTargetItem = {
  value: TextField;
};

/**
 * Will be called during SSG/ISR
 * ! We don't want to use this method because it's used for generating the component on the static page
 * @param {ComponentRendering} rendering
 * @param {LayoutServiceData} layoutData
 * @param {GetStaticPropsContext} context
 */

export default compose<PlanOptionsListingProps>(withStyles())(PlanOptionsListing);
